import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { RETRIEVAL_GUARDIAN_CONFIG } from "../guardianFormConfig";
import RetrievalGuardianPrincipal from "./sections/RetrievalGuardianPrincipal";

/**
 * Principals panel orchestrator for Bond Retrieval guardian forms (Phase 4).
 */
export default function GuardianFormBody({
  bondKind,
  globalData,
  data,
  updateData,
  role,
  isDisabled,
  pii_access,
  allCities,
  allStates,
  professionalGuardians,
  attorneys,
  loadingCitiesStates,
  loadingGuardians,
  loadingAttorneys,
  attorneySearchEnabled = false,
  attorneySearchInput = "",
  setAttorneySearchInput,
  debouncedSearchAttorneys,
  clearAttorneys,
  showSSNStates,
  toggleShowSSN,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  splitEmploymentOccupation,
  handlers,
}) {
  const cfg = RETRIEVAL_GUARDIAN_CONFIG[bondKind];
  const keys = cfg.fieldKeys;
  const countKey = cfg.guardianCountKey || "PrincipalNumber";
  const rawCount = globalData?.basicInformation?.[countKey] ?? 1;
  const numberOfPrincipals = Math.max(1, Number(rawCount) || 1);
  const guardians = Array.isArray(data?.guardians) ? data.guardians : [];

  const {
    updateGuardianAt,
    fetchProfessionalGuardianDetails,
    handleAttorneyHomeAddressChange,
    handleAttorneySelection,
    handleGoogleCallback,
    handleZipCodeChange,
    handleProfessionalGuardianChange,
    handleAttorneyChange,
    handleNameChange,
    handleFieldChange,
    handleEmploymentOccupationChange,
  } = handlers;

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Principals
      </Typography>
      {Array.from({ length: numberOfPrincipals }, (_, i) => (
        <RetrievalGuardianPrincipal
          bondKind={bondKind}
          index={i}
          key={`guardian${i}`}
          guardian={guardians[i] || {}}
          guardians={guardians}
          globalData={globalData}
          data={data}
          updateData={updateData}
          retrievalCfg={cfg}
          keys={keys}
          role={role}
          isDisabled={isDisabled}
          pii_access={pii_access}
          professionalGuardians={professionalGuardians}
          attorneys={attorneys}
          loadingCitiesStates={loadingCitiesStates}
          loadingGuardians={loadingGuardians}
          loadingAttorneys={loadingAttorneys}
          attorneySearchEnabled={attorneySearchEnabled}
          attorneySearchInput={attorneySearchInput}
          setAttorneySearchInput={setAttorneySearchInput}
          debouncedSearchAttorneys={debouncedSearchAttorneys}
          clearAttorneys={clearAttorneys}
          allCities={allCities}
          allStates={allStates}
          showSSNStates={showSSNStates}
          toggleShowSSN={toggleShowSSN}
          GoogleAutoComplete={GoogleAutoComplete}
          LabeledCreatableSelect={LabeledCreatableSelect}
          splitEmploymentOccupation={splitEmploymentOccupation}
          updateGuardianAt={updateGuardianAt}
          fetchProfessionalGuardianDetails={fetchProfessionalGuardianDetails}
          handleProfessionalGuardianChange={handleProfessionalGuardianChange}
          handleAttorneyChange={handleAttorneyChange}
          handleAttorneySelection={handleAttorneySelection}
          handleAttorneyHomeAddressChange={handleAttorneyHomeAddressChange}
          handleGoogleCallback={handleGoogleCallback}
          handleZipCodeChange={handleZipCodeChange}
          handleNameChange={handleNameChange}
          handleFieldChange={handleFieldChange}
          handleEmploymentOccupationChange={handleEmploymentOccupationChange}
        />
      ))}
    </Box>
  );
}

GuardianFormBody.propTypes = {
  bondKind: PropTypes.string.isRequired,
  globalData: PropTypes.shape({
    basicInformation: PropTypes.shape({
      PrincipalNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      GuardianNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      bondAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      is_esignatures: PropTypes.string,
    }),
  }),
  data: PropTypes.shape({ guardians: PropTypes.array }).isRequired,
  updateData: PropTypes.func.isRequired,
  role: PropTypes.string,
  isDisabled: PropTypes.bool,
  pii_access: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  allCities: PropTypes.array.isRequired,
  allStates: PropTypes.array.isRequired,
  professionalGuardians: PropTypes.array.isRequired,
  attorneys: PropTypes.array.isRequired,
  loadingCitiesStates: PropTypes.bool,
  loadingGuardians: PropTypes.bool,
  loadingAttorneys: PropTypes.bool,
  attorneySearchEnabled: PropTypes.bool,
  attorneySearchInput: PropTypes.string,
  setAttorneySearchInput: PropTypes.func,
  debouncedSearchAttorneys: PropTypes.func,
  clearAttorneys: PropTypes.func,
  showSSNStates: PropTypes.object.isRequired,
  toggleShowSSN: PropTypes.func.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  splitEmploymentOccupation: PropTypes.func.isRequired,
  handlers: PropTypes.shape({
    updateGuardianAt: PropTypes.func.isRequired,
    fetchProfessionalGuardianDetails: PropTypes.func.isRequired,
    handleAttorneyHomeAddressChange: PropTypes.func.isRequired,
    handleAttorneySelection: PropTypes.func.isRequired,
    handleGoogleCallback: PropTypes.func.isRequired,
    handleZipCodeChange: PropTypes.func.isRequired,
    handleProfessionalGuardianChange: PropTypes.func.isRequired,
    handleAttorneyChange: PropTypes.func.isRequired,
    handleNameChange: PropTypes.func.isRequired,
    handleFieldChange: PropTypes.func.isRequired,
    handleEmploymentOccupationChange: PropTypes.func.isRequired,
  }).isRequired,
};
